import './CreativeAllocationPicker.scss';
import * as Dfns from 'date-fns/fp';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Modal, Tab, Tabs } from 'react-bootstrap';
import { MdCheckCircle } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Design, Size } from '@Components/designConstants';
import { Spinner } from '@Components/Spinner';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import {
  getIneligibilityLabel,
  getIneligibilityReason,
  PlacementSummary
} from '@Components/CreativeAllocation';
import { useCreativeMap } from '../../../redux/creative';
import { useMap } from '../../../utils/hooks/useData';
import { CreativeAllocationChart } from './CreativeAllocationChart';
import { StagedTagData } from './utils';

const TODAY = Dfns.format('yyyy-MM-dd', new Date());

interface CreativeAllocationPickerProps {
  tags: StagedTagData[];
  setTags: Dispatch<SetStateAction<StagedTagData[]>>;
  onSubmit: () => void;
  onBack?: () => void;
  isEditMode: boolean;
}

export const CreativeAllocationPicker = ({
  tags,
  setTags,
  onSubmit,
  onBack,
  isEditMode
}: CreativeAllocationPickerProps) => {
  const [selectedTag, setSelectedTag] = useState<StagedTagData>(tags[0]);

  const { creativeMap, isciLiveOnDate, loaded } = useCreativeMap({
    company: selectedTag.company,
    mediaTypes: ['audio', 'display', 'streaming']
  });
  // Every creative for the selected tag, annotated with the reason it's
  // ineligible (or null when eligible). Eligibility mirrors bpm-frontend's
  // useWorkspaceOptions predicate — platform/media type, Flashtalking IDs,
  // dimensions, length, and live-on-date. `null` only while the map loads.
  const creativeOptions = useMemo(() => {
    if (!loaded || !creativeMap) return null;
    const date = selectedTag.startDate || TODAY;
    const options = Object.values(creativeMap).map(creative => {
      const ctx = {
        isci: creative.isci,
        date,
        platform: selectedTag.platform,
        isLive: isciLiveOnDate({ isci: creative.isci, date }),
        length: selectedTag.length,
        dimensions: selectedTag.dimensions,
        placementType: selectedTag.placementType,
        adServer: selectedTag.adServer
      };
      const reason = getIneligibilityReason(creative, ctx);
      return {
        isci: creative.isci,
        name: creative.name,
        length: creative.length,
        reason,
        disabledReason: reason ? getIneligibilityLabel(reason, creative, ctx) : undefined
      };
    });
    return options;
  }, [creativeMap, loaded, isciLiveOnDate, selectedTag]);
  const [showAllCreatives, setShowAllCreatives] = useState(false);
  const [formFailMessage, setFormFailMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isEditingRotations, setIsEditingRotations] = useState(isEditMode);
  const [shouldUseSingleCreativeAllocation, setShouldUseSingleCreativeAllocation] = useState(false);
  const [isCopyPanelOpen, setIsCopyPanelOpen] = useState(false);
  const [copyTargetTagNames, setCopyTargetTagNames] = useState<Set<string>>(new Set());

  const existingCreativeAllocations = useMemo(() => {
    const allocationsMap: Record<string, Record<string, number> | undefined> = {};
    for (const tag of tags) {
      allocationsMap[tag.name] = tag.creativeAllocationMap;
    }
    return allocationsMap;
  }, [tags]);

  const [
    tagsToCreativeAllocations,
    setTagsToCreativeAllocationsValue,
    setTagsToCreativeAllocations
  ] = useMap<string, Record<string, number>>(isEditMode ? existingCreativeAllocations : {});

  const isValidAllocation = useCallback(
    (tag: StagedTagData) => {
      const creativeAllocation: Record<string, number> = tagsToCreativeAllocations[tag.name] || {};
      const allocationSum = Object.values(creativeAllocation).reduce((a, b) => a + (b || 0), 0);
      return allocationSum === 100;
    },
    [tagsToCreativeAllocations]
  );

  const attemptSubmit = useCallback(() => {
    const allAllocationsExist = tags.length === Object.keys(tagsToCreativeAllocations).length;
    const allAllocationsValid = tags.reduce((prev, curr) => prev && isValidAllocation(curr), true);

    if (!allAllocationsExist) {
      setFormFailMessage('You are missing allocations for one or more tags.');
      return;
    }
    if (!allAllocationsValid) {
      setFormFailMessage('Creative allocations must sum to 100%');
      return;
    }

    // Persist only non-zero allocations — a creative set to 0% is effectively
    // unpicked and must not reach the staged tag, clickthrough options, or the
    // Flashtalking payload.
    const tagsWithCreativeAllocations = tags.map(tag => {
      const alloc = tagsToCreativeAllocations[tag.name] || {};
      const tidied = Object.fromEntries(Object.entries(alloc).filter(([, v]) => Boolean(v)));
      return { ...tag, creativeAllocationMap: tidied };
    });
    setTags(tagsWithCreativeAllocations);
    setTagsToCreativeAllocations({});
    setIsEditingRotations(false);
  }, [isValidAllocation, setTags, setTagsToCreativeAllocations, tags, tagsToCreativeAllocations]);

  useEffect(() => {
    const tagsHaveCreativeAllocations = tags.reduce(
      (prev, curr) => prev && Boolean(curr.creativeAllocationMap),
      true
    );
    if (tagsHaveCreativeAllocations && !isSubmitted && !isEditingRotations) {
      onSubmit();
      setIsSubmitted(true);
    }
    if (!tagsHaveCreativeAllocations) {
      setIsSubmitted(false);
    }
  }, [onSubmit, tags, isSubmitted, isEditingRotations]);

  const hasMixedDerivedIDs = useMemo(() => new Set(tags.map(t => t.derivedID)).size > 1, [tags]);

  const makeTabTitle = useCallback(
    (tag: StagedTagData) => {
      const properties: string[] = [];
      if (hasMixedDerivedIDs) {
        properties.push(tag.derivedID);
      }
      if (tag.device) {
        properties.push(tag.device);
      }
      if (tag.OS) {
        properties.push(tag.OS);
      }
      if (tag.additionalParam) {
        properties.push(tag.additionalParam);
      }
      if (tag.length) {
        properties.push(`${tag.length}s`);
      }
      if (tag.dimensions && tag.placementType === 'Display') {
        properties.push(tag.dimensions);
      }
      return properties.length ? properties.join(' - ') : tag.name;
    },
    [hasMixedDerivedIDs]
  );

  const setCreativeAllocationValue = useCallback(
    (isci: string, value: number, tagName: string) => {
      const newAlloc = { ...tagsToCreativeAllocations[tagName], [isci]: value };
      if (shouldUseSingleCreativeAllocation) {
        for (const name of tags.map(tag => tag.name)) {
          setTagsToCreativeAllocationsValue(name, newAlloc);
        }
      } else {
        setTagsToCreativeAllocationsValue(tagName, newAlloc);
      }
    },
    [
      setTagsToCreativeAllocationsValue,
      shouldUseSingleCreativeAllocation,
      tags,
      tagsToCreativeAllocations
    ]
  );

  const toggleShouldUseSingleCreativeAllocation = useCallback(
    (currentTagName: string) => {
      setShouldUseSingleCreativeAllocation(prev => !prev);
      const currentTagAlloc = tagsToCreativeAllocations[currentTagName];
      if (currentTagAlloc) {
        for (const name of tags.map(tag => tag.name)) {
          setTagsToCreativeAllocationsValue(name, currentTagAlloc);
        }
      }
    },
    [setTagsToCreativeAllocationsValue, tags, tagsToCreativeAllocations]
  );

  const otherTags = useMemo(
    () => tags.filter(t => t.name !== selectedTag.name),
    [tags, selectedTag]
  );

  const tagsAreHomogeneous = useMemo(
    () =>
      tags.every(t => t.length === tags[0].length) &&
      tags.every(t => t.dimensions === tags[0].dimensions),
    [tags]
  );
  const allOtherTagsSelected =
    otherTags.length > 0 && otherTags.every(t => copyTargetTagNames.has(t.name));

  const toggleCopyTarget = useCallback((tagName: string) => {
    setCopyTargetTagNames(prev => {
      const next = new Set(prev);
      if (next.has(tagName)) {
        next.delete(tagName);
      } else {
        next.add(tagName);
      }
      return next;
    });
  }, []);

  const toggleSelectAllCopyTargets = useCallback(() => {
    setCopyTargetTagNames(allOtherTagsSelected ? new Set() : new Set(otherTags.map(t => t.name)));
  }, [allOtherTagsSelected, otherTags]);

  const closeCopyPanel = useCallback(() => {
    setIsCopyPanelOpen(false);
    setCopyTargetTagNames(new Set());
  }, []);

  const applyCopyToSelectedTags = useCallback(() => {
    const sourceAlloc = tagsToCreativeAllocations[selectedTag.name];
    if (!sourceAlloc) return;
    for (const tagName of copyTargetTagNames) {
      setTagsToCreativeAllocationsValue(tagName, { ...sourceAlloc });
    }
    closeCopyPanel();
  }, [
    closeCopyPanel,
    copyTargetTagNames,
    selectedTag,
    setTagsToCreativeAllocationsValue,
    tagsToCreativeAllocations
  ]);

  useEffect(() => {
    closeCopyPanel();
  }, [selectedTag.name, closeCopyPanel]);

  return (
    <div className='creative-allocation-picker'>
      <Modal.Header closeButton>
        <Modal.Title>Set Creative Rotations</Modal.Title>
      </Modal.Header>
      <Modal.Body className='modal-body'>
        <div className='workspace'>
          <div className='picker-toolbar'>
            <PlacementSummary
              platform={selectedTag.platform}
              placementType={selectedTag.placementType}
              length={selectedTag.length}
              dimensions={
                selectedTag.placementType === 'Display' ? selectedTag.dimensions : undefined
              }
              date={selectedTag.startDate}
            />
            <ToggleSwitch
              label='Show all creatives'
              checked={showAllCreatives}
              onChange={setShowAllCreatives}
              infoTooltipText='Show creatives that are ineligible for this placement, disabled with the reason why.'
            />
          </div>
          <Tabs
            defaultActiveKey={tags[0].name}
            onSelect={tagName => {
              const tag = tags.find(t => t.name === tagName);
              if (tag) {
                setSelectedTag(tag);
              }
            }}
          >
            {tags.map(tag => {
              const tabTitle = makeTabTitle(tag);
              const visibleOptions =
                creativeOptions?.filter(opt => showAllCreatives || !opt.reason) ?? [];
              let cardContent;
              if (!creativeOptions) {
                cardContent = (
                  <div className='allocation-loading'>
                    <Spinner />
                  </div>
                );
              } else if (visibleOptions.length === 0) {
                cardContent = (
                  <div className='no-creatives-message'>
                    No creatives are live or match this placement&apos;s constraints.
                    {!showAllCreatives &&
                      creativeOptions.length > 0 &&
                      ' Turn on “Show all creatives” to see what’s being filtered and why.'}
                  </div>
                );
              } else {
                cardContent = (
                  <CreativeAllocationChart
                    company={selectedTag.company}
                    data={visibleOptions.map(opt => ({
                      isci: opt.isci,
                      name: opt.name,
                      length: opt.length,
                      value: tagsToCreativeAllocations[tag.name]?.[opt.isci] || 0,
                      disabled: Boolean(opt.reason),
                      disabledReason: opt.disabledReason
                    }))}
                    onChange={(isci, value) => setCreativeAllocationValue(isci, value, tag.name)}
                  />
                );
              }
              return (
                <Tab
                  className='tab'
                  key={tag.name}
                  eventKey={tag.name}
                  title={
                    <div className='tab-title'>
                      {tabTitle}
                      {isValidAllocation(tag) && (
                        <div className='valid-indicator'>
                          <MdCheckCircle />
                        </div>
                      )}
                    </div>
                  }
                >
                  {tags.length > 1 && tagsAreHomogeneous ? (
                    <div className='creative-allocation-checkbox-container'>
                      <Checkbox
                        checked={shouldUseSingleCreativeAllocation}
                        onChange={() => toggleShouldUseSingleCreativeAllocation(tag.name)}
                        label='Set the same allocation for all tags'
                      />
                      {!shouldUseSingleCreativeAllocation && (
                        <Button
                          className='copy-allocation-button'
                          appearance={Appearance.EMPTY}
                          size={Size.SM}
                          disabled={!tagsToCreativeAllocations[tag.name]}
                          onClick={() => setIsCopyPanelOpen(open => !open)}
                        >
                          Copy this allocation to other placements...
                        </Button>
                      )}
                    </div>
                  ) : (
                    tags.length > 1 && (
                      <p className='copy-allocation-explainer'>
                        Each placement has different lengths or dimensions, so allocations must be
                        set individually per tab.
                      </p>
                    )
                  )}
                  {isCopyPanelOpen && tag.name === selectedTag.name && (
                    <div className='copy-allocation-panel'>
                      <div className='copy-allocation-panel-header'>
                        <Checkbox
                          className='copy-allocation-panel-row'
                          label={
                            <span className='copy-allocation-panel-select-all-label'>
                              Select all
                            </span>
                          }
                          checked={allOtherTagsSelected}
                          onChange={toggleSelectAllCopyTargets}
                        />
                      </div>
                      <div className='copy-allocation-panel-list'>
                        {otherTags.map(otherTag => (
                          <Checkbox
                            key={otherTag.name}
                            className='copy-allocation-panel-row'
                            label={makeTabTitle(otherTag)}
                            checked={copyTargetTagNames.has(otherTag.name)}
                            onChange={() => toggleCopyTarget(otherTag.name)}
                          />
                        ))}
                      </div>
                      <div className='copy-allocation-panel-footer'>
                        <Button
                          appearance={Appearance.OUTLINED}
                          size={Size.SM}
                          onClick={closeCopyPanel}
                        >
                          Cancel
                        </Button>
                        <Button
                          size={Size.SM}
                          disabled={copyTargetTagNames.size === 0}
                          onClick={applyCopyToSelectedTags}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  )}
                  <Card className='workspace-card'>
                    <div className='workspace-card-inner'>{cardContent}</div>
                  </Card>
                </Tab>
              );
            })}
          </Tabs>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {formFailMessage && (
          <Alert variant='danger' className='alert'>
            {formFailMessage}
          </Alert>
        )}
        {onBack && (
          <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onBack}>
            Back
          </Button>
        )}
        <Button onClick={attemptSubmit}>Done</Button>
      </Modal.Footer>
    </div>
  );
};
